import {FC, Fragment, ReactNode, useCallback, useEffect, useMemo, useState} from "react";
import {DynamicDataTableFilter, FilterSettings, TableProps} from "./types";
import {usePersistentState} from "../../util/usePersistentState";
import {
  calculatePageCount,
  getPagination,
  paginateRows,
  PAGINATION_LIMIT_DEFAULT,
  PAGINATION_LIMIT_OPTIONS
} from "./DataPaginate";
import {dataSearch, enrichRowWithSearchableString} from "./DataSearch";
import {useNavigate, useSearchParams} from "react-router";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowDown, faArrowUp} from "@fortawesome/pro-light-svg-icons";
import {sortRows} from "./DataSort";
import DataTable from "./DataTable";
import {faChevronLeft, faChevronRight} from "@fortawesome/free-solid-svg-icons";
// import {Input} from "@ventumsoftware/react-toolkit";
import {Select} from "../form/Select";
import {transformToFancyAutocompleteOptions, transformToStringMapObject} from "./transformOptions";
import {useFilterSettings} from "./useFilterSettings";
import { Autocomplete } from "../data/auto-complete/Autocomplete";
import {FancyAutocomplete} from "../data/auto-complete/FancyAutocomplete";
import {Input} from "../form/Input";

interface DynamicDataTableProps<T> extends TableProps<T> {
  searchableProperties: Array<keyof T>
  availableFilters: DynamicDataTableFilter<T>[]
}

export default function DynamicDataTable<T>(props: DynamicDataTableProps<T>): ReactNode {
  const [searchParams] = useSearchParams()
  const [search, setSearch] = useState<string>('')
  const navigate = useNavigate()
  const {setFilterValue, getFilterValue} = useFilterSettings(searchParams, props.availableFilters, navigate)
  const [page, setPage] = useState(() => {
    const value = Number(getFilterValue('page'));
    return Number.isFinite(value) ? value : 0;
  });
  const [paginationLimit, setPaginationLimit] = usePersistentState<number>("paginationLimit", PAGINATION_LIMIT_DEFAULT)

  const [sortColumn, setSortColumn] = useState<keyof T | null>(null)
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC" | null>(null)


  const searchableData = useMemo(() => {
    return enrichRowWithSearchableString(props.data, props.searchableProperties)
  }, [props.data, props.searchableProperties])

  const filteredData = useMemo(() => {
    return searchableData.filter(row => {
      for (const filter of props.availableFilters) {
        const callback = filter.filterCallback ?? ((row: T, value: string) => row[filter.property] === value)
        const result = callback(row, getFilterValue(filter.property))
        if (!result) {
          return false
        }
      }
      // All filters have passed
      return true
    })
  }, [searchableData, props.availableFilters, getFilterValue])

  const changeSortSettings = useCallback(function <T>(updatedColumn: keyof T) {
    // @ts-ignore
    if (sortColumn !== updatedColumn) {
      // @ts-ignore
      setSortColumn(updatedColumn)
      setSortOrder("ASC")
      return
    }
    // Cycle the sort order
    if (sortOrder === "ASC") {
      setSortOrder("DESC")
    } else if (sortOrder === "DESC") {
      setSortOrder(null)
    } else {
      setSortOrder("ASC")
    }
  }, [sortColumn, sortOrder])

  const sortingHeaderMapper = useCallback(function <T>(props: TableProps<T>): ReactNode[] {
    const headers = props.columns.map((column) =>
      <th
        key={column.header}
        className={`${props.compact ? 'px-3 py-3' : 'px-6 py-3'} bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap cursor-pointer hover:bg-gray-100`}
        onClick={() => changeSortSettings<T>(column.property)}
      >
        <div className={"flex items-center justify-between"}>
          <span>{column.header}</span>
          {/* @ts-ignore */}
          {sortColumn === column.property && sortOrder !== null &&
            <FontAwesomeIcon icon={sortOrder === "ASC" ? faArrowUp : faArrowDown}/>}
        </div>
      </th>
    )
    if (props.onClickRow) {
      headers.push(<th key={'_actions'}
                       className={(props.compact ? 'px-3 py-3' : 'px-6 py-3') + " bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap"}></th>)
    }
    return headers
  }, [changeSortSettings, sortColumn, sortOrder, props.columns, props.onClickRow])


  // SEARCH
  const searchedData = useMemo(() => {
    return dataSearch(filteredData, search)
  }, [filteredData, search])

  const paginatedData = useMemo(() => {
    return sortRows(searchedData, sortOrder, sortColumn)
    // return paginateRows(sortedData, page, paginationLimit)
  }, [searchedData, sortOrder, sortColumn, page])

  const pageCount = useMemo(() => {
    return calculatePageCount(paginatedData, paginationLimit)
  }, [paginationLimit, paginatedData])

  useEffect(() => {
    if (page >= pageCount && pageCount > 0) {
      setPage(pageCount - 1)
    } else if (page < 0) {
      setPage(0)
    }
  }, [page, pageCount]);

  const pages = useMemo(() => {
    return getPagination(page, pageCount, paginationLimit)
  }, [page, pageCount, paginationLimit])

  const searchedAndPaginatedData = useMemo(() => {
    return paginateRows(paginatedData, page, paginationLimit)
  }, [page, paginatedData, paginationLimit])

  const paginationButtonBaseStyle = 'rounded-xs h-8 w-8 flex items-center justify-center font-medium'
  return <>
    <div className={'flex flex-row space-x-4 mb-4'}>
      {props.availableFilters.map((filter, i) => {
          if (filter.type === 'autocomplete') {
            return <div key={i}>
              <Autocomplete label={filter.title} options={transformToStringMapObject(filter.options)}
                            value={getFilterValue(filter.property)} onChange={(v) => setFilterValue(filter.property, v)}/>
            </div>
          } else if (filter.type === 'fancy-autocomplete') {
            return <div key={i}>
              <FancyAutocomplete label={filter.title} options={transformToFancyAutocompleteOptions(filter.options)}
                                 value={getFilterValue(filter.property)}
                                 onChange={(v) => setFilterValue(filter.property, v)}/>
            </div>
          } else {
            return <div key={i}>
              <Autocomplete label={filter.title} options={transformToStringMapObject(filter.options)}
                            value={getFilterValue(filter.property)} onChange={(v) => setFilterValue(filter.property, v)}/>
            </div>
          }
        }
      )}
    </div>
    <div className={"flex flex-row space-x-4 mb-4 items-center"}>
      <Input type={'text'} label={''} placeholder={'Zoeken...'} value={search} onChange={setSearch} />
      <div className={'mr-2'}>
        <Select label={'Per page'} inline options={Object.fromEntries(PAGINATION_LIMIT_OPTIONS.map(i => [String(i),String(i)]))} value={String(paginationLimit)} onChange={(newLimit) => setPaginationLimit(Number(newLimit))} />
      </div>
    </div>
    <DataTable {...props} data={searchedAndPaginatedData} headerMapper={sortingHeaderMapper} />
    {pageCount > 1 ? <div className={"flex"}>
      <div className={"flex items-center justify-start mt-4 border border-slate-200 rounded-lg text-brand-800 p-1 bg-white space-x-1"}>
        <button className={`${paginationButtonBaseStyle} ${page <= 0 ? 'text-slate-300' : 'hover:bg-blue-100 hover:text-blue-600'}`} disabled={page <= 0} onClick={() => setPage(p => {
          setFilterValue('page', (p-1).toString())
          return p - 1
        })}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        {pages.map((i, k, l) => {
          return <Fragment key={k}>
            {k > 0 && (i - l[k - 1]) > 1 ? <span className={'text-slate-400'}>...</span> : null}
            <button className={`${paginationButtonBaseStyle} hover:bg-blue-100 hover:text-blue-600 ${page === i ? 'bg-blue-50 border border-blue-500' : ''}`} onClick={() => {
              setFilterValue('page', i.toString())
              setPage(i)
            }}>
              {i+1}
            </button>
          </Fragment>
        })}
        <button className={`${paginationButtonBaseStyle} ${(page+1) >= pageCount ? 'text-slate-300' : 'hover:bg-blue-100 hover:text-blue-600'}`} disabled={(page+1) >= pageCount} onClick={() => setPage(p => {
          setFilterValue('page', (p+1).toString())
          return p + 1
        })}>
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
    </div> : null}
  </>
}
